/**
 * Starting a session: shows the QR the phone scans, the link to share with
 * other browsers, and the token the Mac agent needs. Also lists past meetings,
 * since the owner key that starts one can open any of them.
 *
 * The menubar app can start a meeting by itself ("New meeting"), so this page
 * is the second way in rather than the only one.
 *
 * The QR carries only the short-lived pairing code — never a token — so a photo
 * of someone's screen is useless three minutes later.
 */
import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  ApiError,
  clearOwnerKey,
  listSessions,
  mintViewerToken,
  refreshPairingCode,
  startSession,
  storeOwnerKey,
  storedOwnerKey,
  type PastSession,
  type StartedSession,
} from '../lib/api.js';

function localizeUrl(urlStr: string | null): string | null {
  if (!urlStr) return null;
  try {
    const parsed = new URL(urlStr);
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      return `${window.location.origin}${parsed.pathname}${parsed.search}`;
    }
    return urlStr;
  } catch {
    return urlStr;
  }
}

export function StartPage(): JSX.Element {
  const [ownerKey, setOwnerKey] = useState(() => storedOwnerKey() ?? '');
  const [session, setSession] = useState<StartedSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [pairing, setPairing] = useState<{ joinCode: string; expiresAt: number; controllerUrl: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const rawControllerUrl = pairing?.controllerUrl ?? session?.controllerUrl ?? null;
  const controllerUrl = localizeUrl(rawControllerUrl);
  const joinCode = pairing?.joinCode ?? session?.joinCode ?? null;
  const expiresAt = pairing?.expiresAt ?? session?.joinCodeExpiresAt ?? null;
  const viewerUrl = session ? localizeUrl(session.viewerUrl) ?? session.viewerUrl : null;
  const relayUrl = window.location.port === '5173' ? 'http://127.0.0.1:8080' : window.location.origin;

  useEffect(() => {
    if (!controllerUrl) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(controllerUrl, { width: 320, margin: 1, errorCorrectionLevel: 'M' }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [controllerUrl]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const started = await startSession(ownerKey.trim());
      storeOwnerKey(ownerKey);
      setSession(started);
      setPairing(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearOwnerKey();
        setError('That owner key was rejected.');
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }, [ownerKey]);

  const newCode = useCallback(async () => {
    if (!session) return;
    try {
      setPairing(await refreshPairingCode(session.sessionId, ownerKey.trim()));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [session, ownerKey]);

  const copy = useCallback(async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1_500);
    } catch {
      setError('Could not reach the clipboard; select and copy by hand.');
    }
  }, []);

  if (!session) {
    return (
      <div className="start">
        <h1>Live Transcription</h1>
        <p className="muted">
          Starting a session needs the control plane&apos;s owner key. It is kept in this browser and never
          included in a share link.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void start();
          }}
        >
          <label htmlFor="owner-key">Owner key</label>
          <input
            id="owner-key"
            type="password"
            autoComplete="off"
            value={ownerKey}
            onChange={(event) => setOwnerKey(event.target.value)}
            placeholder="OWNER_API_KEY"
          />
          <button type="submit" className="primary" disabled={busy || ownerKey.trim().length === 0}>
            {busy ? 'Starting…' : 'Start a session'}
          </button>
        </form>
        {error ? <p className="error">{error}</p> : null}
        {storedOwnerKey() ? <PastMeetings ownerKey={storedOwnerKey()!} /> : null}
      </div>
    );
  }

  return (
    <div className="start started">
      <h1>Session ready</h1>

      <section className="pair">
        <h2>Scan with your phone</h2>
        {qrDataUrl ? <img src={qrDataUrl} alt="Pairing QR code" width={260} height={260} /> : <p>Generating…</p>}
        <p className="code">{joinCode}</p>
        <p className="muted">
          The code expires {expiresAt ? `at ${new Date(expiresAt).toLocaleTimeString()}` : 'shortly'}. The phone
          becomes the controller: it scrolls and points for everyone.
        </p>
        <button type="button" onClick={() => void newCode()}>
          New code
        </button>
      </section>

      <section className="share">
        <h2>Open elsewhere</h2>
        <p className="muted">Any other browser opening this link joins as a viewer.</p>
        <div className="row">
          <code>{viewerUrl}</code>
          <button type="button" onClick={() => void copy('viewer', viewerUrl ?? '')}>
            {copied === 'viewer' ? 'Copied' : 'Copy'}
          </button>
        </div>
        {viewerUrl ? (
          <a className="primary button" href={new URL(viewerUrl).pathname + new URL(viewerUrl).search}>
            Open the conversation here
          </a>
        ) : null}
      </section>

      <section className="agent">
        <h2>Mac capture agent</h2>
        <p className="muted">
          Only needed for a session started here: the menubar app&apos;s New meeting does all of this by itself.
          Otherwise paste these into its Settings. It records to disk from the first second, so nothing is lost
          while transcription starts.
        </p>
        <dl>
          <dt>Relay base URL</dt>
          <dd className="row">
            <code>{relayUrl}</code>
            <button type="button" onClick={() => void copy('relay', relayUrl)}>
              {copied === 'relay' ? 'Copied' : 'Copy'}
            </button>
          </dd>
          <dt>Session id</dt>
          <dd className="row">
            <code>{session.sessionId}</code>
            <button type="button" onClick={() => void copy('session', session.sessionId)}>
              {copied === 'session' ? 'Copied' : 'Copy'}
            </button>
          </dd>
          <dt>Agent token</dt>
          <dd className="row">
            <code className="truncate">{session.agentToken}</code>
            <button type="button" onClick={() => void copy('agent', session.agentToken)}>
              {copied === 'agent' ? 'Copied' : 'Copy'}
            </button>
          </dd>
        </dl>
      </section>

      {error ? <p className="error">{error}</p> : null}
    </div>
  );
}

/** Recent meetings, each opening as a viewer with a freshly minted link. */
function PastMeetings(props: { ownerKey: string }): JSX.Element | null {
  const [sessions, setSessions] = useState<PastSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSessions(props.ownerKey)
      .then((list) => !cancelled && setSessions(list))
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [props.ownerKey]);

  const open = useCallback(
    async (sessionId: string) => {
      try {
        const { viewerToken } = await mintViewerToken(sessionId, props.ownerKey);
        window.location.assign(`/s/${encodeURIComponent(sessionId)}?t=${encodeURIComponent(viewerToken)}`);
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [props.ownerKey],
  );

  if (error) return <p className="error">Could not load past meetings: {error}</p>;
  if (!sessions || sessions.length === 0) return null;

  return (
    <section className="history">
      <h2>Past meetings</h2>
      <ul>
        {sessions.map((session) => (
          <li key={session.sessionId}>
            <button type="button" onClick={() => void open(session.sessionId)}>
              <span>{new Date(session.startedAt ?? session.createdAt).toLocaleString()}</span>
              <span className="muted">{describe(session)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function describe(session: PastSession): string {
  if (session.status === 'live') return 'live now';
  if (!session.startedAt) return 'never started';
  if (!session.endedAt) return session.status;
  const minutes = Math.round((Date.parse(session.endedAt) - Date.parse(session.startedAt)) / 60_000);
  return minutes < 1 ? 'under a minute' : `${minutes} min`;
}

